import { InfoEmployeds } from 'components/InfoEmployeds';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ContentLoader from 'react-content-loader';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetActionForOrdersNotifiedWorkersQuery,
  useActionForOrdersNotifyMutation,
  useGetOrderQuery,
  useGetResponceSelectWorkerQuery,
  useGetWorkersQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { RootState } from 'redux/store';
import { InputSearch } from 'ui/InputSearch';
import { getFormatPhone } from 'utils/getFotmatPhone';
import styles from './OrderExecutor.module.scss';
import { ReactComponent as CheckIcon } from './img/check.svg';
import { ReactComponent as AlertIcon } from './img/alert.svg';
import { ReactComponent as WarningIcon } from './img/iconWarning.svg';
import { ReactComponent as IconError } from './img/iconError.svg';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { ReactComponent as IconWork } from './img/iconWork.svg';
import { ReactComponent as IconSend } from './img/iconSend.svg';
import { ReactComponent as IconSendFilled } from './img/iconSendFilled.svg';
import { Avatar } from 'components/Avatar';
import moment from 'moment';
import MinorAgeChecker from 'components/MinorAgeChecker';
export { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { GetWorkersArgs, ResponseGetWorkerType } from 'redux/Api/ordersApi/type';
import { EmployedsFilterParams, Filters } from 'pages/EmployedsContent/Filters/Filters';
import { useGetFiltersEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useMediaQuery, useElementSize } from '@mantine/hooks';
import { MobileMenu } from 'pages/EmployedsContent/Sort/SortFilterMobile';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { HandleFieldKey, HandleFieldValue } from 'components/CreateOrderForm/types';
import { Group, Loader, Switch } from '@mantine/core';
import { TabMenu } from 'components/TabMenu';
import { AppTooltip } from 'ui/AppTooltip';
import { FormatAmount } from 'ui/FormatAmount';
import { formatEndDate } from 'utils/date';
import { enumerate } from 'utils/strActions';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ModalApproveWorker } from 'components/ModalApproveWorker';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';

interface OrderExecutorTypes {
  errors?: {
    employee?: string;
  };
  details?: boolean;
  clearErrorForField?: (key: string | number) => void;
}

type SearchParamsState = GetWorkersArgs | undefined;

const mobileMenuFiltersOnlyStub = () => undefined;

export const OrderExecutor: FC<OrderExecutorTypes> = ({
  errors,
  details,
  clearErrorForField,
}) => {
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const { data: dataFilters } = useGetFiltersEmployedsQuery();
  const { data: profileData } = useGetProfileQuery();
  const [textSuccessErrorInfo, setTextSuccessErrorInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'employees' | 'responses'>('employees');
  const navigate = useNavigate();
  const [showModalApproveWorker, setShowModalApproveWorker] = useState(false);
  const [pendingWorkerId, setPendingWorkerId] = useState<number | null>(null);
  const { ref, width } = useElementSize();
  const isMediumWidth =
    useMediaQuery('(min-width: 1200px) and (max-width: 1406px)') ?? false;
  const isSmallWidth = useMediaQuery('(max-width: 767px)') ?? false;

  const dataFiltersMobile = useMemo(() => {
    const f = dataFilters?.filters;
    if (!f) return undefined;
    const rows = (items: { keyword: string; title: string }[]) =>
      items?.map((item, i) => ({
        id: i + 1,
        keyword: item.keyword,
        label: item.title,
      }));
    return {
      genders: rows(f.genders),
      issues: rows(f.issues),
      nationalities: rows(f.nationalities),
      contracts: rows(f.contracts),
      categories: rows(f.categories),
    };
  }, [dataFilters]);

  const dispatch = useDispatch();
  const { id } = useParams();
  const { data, isLoading } = useGetOrderQuery({ id });
  const [pageEmployees, setPageEmployees] = useState(1);
  const [pageResponses, setPageResponses] = useState(1);
  const [searchParams, setSearachParams] = useState<SearchParamsState>(undefined);
  const {
    data: workersData,
    isLoading: workersDataIsLoading,
    isFetching: workersFetching,
  } = useGetWorkersQuery({
    ...searchParams,
    page: pageEmployees,
    per_page: 20,
  });
  const [actionForOrdersNotify, { isLoading: isLoadingNotify }] =
    useActionForOrdersNotifyMutation();
  const [notifyingWorkerId, setNotifyingWorkerId] = useState<number | null>(null);
  const { data: notifiedWorkersData } = useGetActionForOrdersNotifiedWorkersQuery(
    {
      order_id: id ?? '',
    },
    { skip: !id },
  );

  const handleNotifyWorker = async (workerId: number) => {
    if (!workerId || isLoadingNotify || notifyingWorkerId) return;

    setNotifyingWorkerId(workerId);
    try {
      await actionForOrdersNotify({
        order_id: id ?? '',
        worker_id: workerId.toString(),
      }).unwrap();
      setTextSuccessErrorInfo('Пользователь оповещён');
    } catch (error) {
      console.error(error);
      setNotifyingWorkerId(null);
      setTextSuccessErrorInfo('Ошибка при оповещении пользователя');
    }
  };

  useEffect(() => {
    if (
      notifyingWorkerId != null &&
      notifiedWorkersData?.worker_ids?.includes(notifyingWorkerId)
    ) {
      setNotifyingWorkerId(null);
    }
  }, [notifiedWorkersData, notifyingWorkerId]);

  const needsResponseWorkers =
    Boolean(details) && data?.status?.value === 'order_published' && !data?.worker?.id;

  const {
    data: responseWorkersData,
    isLoading: isLoadingResponseWorkers,
    isFetching: isFetchingResponseWorkers,
  } = useGetResponceSelectWorkerQuery(
    { id: id!, ...searchParams, page: pageResponses, per_page: 50 },
    { skip: !id || isLoading || !needsResponseWorkers },
  );

  const [filtersParams, setFiltersParams] = useState<EmployedsFilterParams>({
    genders: [],
    issues: [],
    nationalities: [],
    categories: [],
    contracts: [],
    date_add: undefined,
  });

  const applySearchParams = (next: SearchParamsState) => {
    if (!setSearachParams) return;
    setPageEmployees(1);
    setPageResponses(1);
    setSearachParams(next);
  };

  const convertFiltersToApiParams = (filters: EmployedsFilterParams) => {
    return {
      genders: filters.genders,
      issues: filters.issues,
      categories: filters.categories?.map((c) => c.id),
      nationalities: filters.nationalities,
      contracts: filters.contracts,
      date_add: filters.date_add,
    };
  };

  const buildMergedParams = (nextSearch: string, nextFilters: EmployedsFilterParams) => {
    const hasSearch = Boolean(nextSearch.trim());
    const hasAnyFilters =
      (nextFilters.genders?.length ?? 0) > 0 ||
      (nextFilters.issues?.length ?? 0) > 0 ||
      (nextFilters.nationalities?.length ?? 0) > 0 ||
      (nextFilters.categories?.length ?? 0) > 0 ||
      (nextFilters.contracts?.length ?? 0) > 0 ||
      nextFilters.date_add?.start != null ||
      nextFilters.date_add?.end != null;

    if (!hasSearch && !hasAnyFilters) return undefined;

    const apiFilters = convertFiltersToApiParams(nextFilters);

    return {
      ...apiFilters,
      ...(hasSearch ? { search: nextSearch.trim() } : {}),
    };
  };

  const readyWorkersList = workersData?.data ?? [];
  const responseWorkersList = Array.isArray(responseWorkersData)
    ? responseWorkersData
    : responseWorkersData?.data ?? [];

  const workersList =
    details && activeTab === 'responses' ? responseWorkersList : readyWorkersList;

  const responsesCount = responseWorkersData?.meta?.assigned_count || 0;

  const orderWorker = data?.worker?.id
    ? [...readyWorkersList, ...responseWorkersList].find((el) => el.id === data.worker.id)
    : undefined;

  const isCanceled =
    data?.status?.value === 'order_canceled' ||
    data?.status?.value === 'order_canceled_by_platform';

  const canPickWorker = !isCanceled && (Boolean(data?.is_draft) || !data?.worker?.id);

  const favoriteWorkerId = responseWorkersList.find((worker) => worker.is_selected)?.id;
  const didPreselectFavoriteRef = useRef(false);
  const lastFavoriteWorkerIdRef = useRef<number | undefined>(undefined);

  const selectedWorker = canPickWorker
    ? readyWorkersList.find((worker) => worker.id === infoOrderDetail.worker_id) ??
      responseWorkersList.find((worker) => worker.id === infoOrderDetail.worker_id)
    : (data?.worker as unknown as ResponseGetWorkerType | undefined);

  const [searchValue, setSearchValue] = useState('');

  const handleFieldChange = useCallback(
    (key: HandleFieldKey, value: HandleFieldValue) => {
      dispatch(setInfoOrderDetail({ key, value }));
    },
    [dispatch],
  );

  useEffect(() => {
    didPreselectFavoriteRef.current = false;
    lastFavoriteWorkerIdRef.current = undefined;
  }, [id]);

  useEffect(() => {
    if (!canPickWorker || !favoriteWorkerId) {
      return;
    }

    const previousFavoriteId = lastFavoriteWorkerIdRef.current;
    const favoriteChanged =
      previousFavoriteId != null && previousFavoriteId !== favoriteWorkerId;

    lastFavoriteWorkerIdRef.current = favoriteWorkerId;

    if (favoriteChanged) {
      if (infoOrderDetail.worker_id !== favoriteWorkerId) {
        handleFieldChange('worker_id', favoriteWorkerId);
      }
      didPreselectFavoriteRef.current = true;
      return;
    }

    if (didPreselectFavoriteRef.current) {
      return;
    }
    if (infoOrderDetail.worker_id != null) {
      didPreselectFavoriteRef.current = true;
      return;
    }
    handleFieldChange('worker_id', favoriteWorkerId);
    didPreselectFavoriteRef.current = true;
  }, [canPickWorker, favoriteWorkerId, handleFieldChange, infoOrderDetail.worker_id]);

  const isDraftOrCreate = !details || Boolean(data?.is_draft);

  useEffect(() => {
    if (isDraftOrCreate && infoOrderDetail.worker_id) {
      handleFieldChange('is_marketplace', false);
    }
  }, [infoOrderDetail.worker_id, handleFieldChange, isDraftOrCreate]);

  const hasNextPageEmployees = workersData?.meta
    ? workersData.meta.current_page < workersData.meta.last_page
    : false;

  const [intersectionRefEmployees, { rootRef: tableScrollRootRefEmployees }] =
    useInfiniteScroll({
      loading: Boolean(workersDataIsLoading || workersFetching),
      hasNextPage: Boolean(hasNextPageEmployees),
      onLoadMore: () => setPageEmployees((p) => p + 1),
      rootMargin: '0px 0px 160px 0px',
      disabled: activeTab !== 'employees',
    });

  const hasNextPageResponses = responseWorkersData?.meta
    ? responseWorkersData.meta.current_page < responseWorkersData.meta.last_page
    : false;

  const [intersectionRefResponses, { rootRef: tableScrollRootRefResponses }] =
    useInfiniteScroll({
      loading: Boolean(isLoadingResponseWorkers || isFetchingResponseWorkers),
      hasNextPage: Boolean(hasNextPageResponses),
      onLoadMore: () => setPageResponses((p) => p + 1),
      rootMargin: '0px 0px 160px 0px',
      disabled: activeTab !== 'responses',
    });

  const tableListRef = useRef<HTMLDivElement | null>(null);

  const setTableListRef = useCallback(
    (node: HTMLDivElement | null) => {
      tableListRef.current = node;
      if (activeTab === 'employees') {
        tableScrollRootRefEmployees(node);
        tableScrollRootRefResponses(null);
      } else {
        tableScrollRootRefResponses(node);
        tableScrollRootRefEmployees(null);
      }
    },
    [activeTab, tableScrollRootRefEmployees, tableScrollRootRefResponses],
  );

  const handleTabChange = useCallback((key: string) => {
    const nextTab = key as 'employees' | 'responses';
    setActiveTab(nextTab);
    if (tableListRef.current) {
      tableListRef.current.scrollTop = 0;
    }
  }, []);

  if (data?.status?.value === 'moderation_in_progress') return null;
  if (isLoading) return null;

  const selectWorker = (workerId: number | null) => {
    dispatch(setInfoOrderDetail({ key: 'worker_id', value: workerId }));
    if (workerId) {
      clearErrorForField?.('worker_id');
    }
  };

  const worker = selectedWorker as ResponseGetWorkerType | undefined;
  const workerForContract = workersData?.data?.find((el) => el.id === worker?.id);

  const isHasSignedContract =
    workerForContract?.unified_contract?.status === 'signed_by_worker';
  const isHasSignedContracts = workerForContract?.unified_contracts?.filter(
    (contract) => contract.status === 'signed_by_worker',
  );

  const handleToggleSelling = (value?: boolean) => {
    handleFieldChange(
      'is_marketplace',
      typeof value === 'boolean' ? value : !infoOrderDetail.is_marketplace,
    );
  };

  const hideWorkersForMarketplace =
    isDraftOrCreate && Boolean(infoOrderDetail.is_marketplace);
  const showMarketplaceSwitch =
    Boolean(profileData?.features?.marketplace) &&
    isDraftOrCreate &&
    !infoOrderDetail.worker_id;

  const isListLoading =
    details && activeTab === 'responses'
      ? Boolean(isLoadingResponseWorkers)
      : workersDataIsLoading;

  const isListFetching =
    details && activeTab === 'responses'
      ? Boolean(isLoadingResponseWorkers) ||
        (isFetchingResponseWorkers && pageResponses === 1)
      : workersDataIsLoading || (workersFetching && pageEmployees === 1);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (!e.target.value.trim()) {
      applySearchParams(buildMergedParams('', filtersParams));
    }
  };

  const handleSearch = () => {
    applySearchParams(buildMergedParams(searchValue, filtersParams));
  };

  const displayedWorkersList = workersList;

  const handleShowLastOrder = (id: number, name: string) => {
    localStorage.setItem(
      'ordersFilters',
      JSON.stringify({
        type_ids: [],
        payment_type_ids: [],
        customer_ids: [],
        worker_ids: [{ id: id.toString(), name }],
        preset_ids: [],
        statuses: [],
        amount_from: undefined,
        amount_to: undefined,
        numbers_range: undefined,
        date_start_range: undefined,
        date_end_range: undefined,
        create_date_range: undefined,
        date_paid_range: {
          start: moment().subtract(3, 'months').format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
        },
        act: undefined,
        receipts: undefined,
        fns_receipts_seen_status: undefined,
      }),
    );
    navigate(`/orders`);
  };

  const getWorkerStatus = (worker: ResponseGetWorkerType) => {
    if (worker.is_in_inn_stoplist) {
      return 'Бывший сотрудник';
    }

    if (
      worker.worker_app.nationality !== 'Россия' &&
      worker.unified_contract?.status !== 'signed_by_worker' &&
      activeTab === 'responses'
    ) {
      return 'Требуется уведомить МВД';
    }

    if (worker.is_in_stoplist) {
      return 'Находится в стоп листе';
    }

    if (worker.has_orders_in_3_prev_months) {
      return 'Заказы в последние 3 месяца';
    }
  };

  return (
    <div ref={ref} className={styles.root}>
      <div className={styles.employees}>
        <div className={styles.employees_inner__top}>
          <p className={styles.employees_inner__title}>Исполнители</p>
        </div>
        {showMarketplaceSwitch && (
          <Group gap='8px' h='40px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                  cursor: 'pointer',
                },
                track: {
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: infoOrderDetail.is_marketplace
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={Boolean(infoOrderDetail.is_marketplace)}
              onChange={() => {
                handleToggleSelling();
              }}
              label='Опубликовать для всех'
            />
            <AppTooltip label='Вы будете получать отклики на заказ и сможете выбрать подходящего исполнителя'>
              <InfoIcon style={{ cursor: 'pointer' }} />
            </AppTooltip>
          </Group>
        )}

        {!worker && (
          <div className={styles.infoWorker}>
            Идет подбор исполнителя, можешь оповестить исполнителя
          </div>
        )}

        {needsResponseWorkers && !worker && (
          <>
            <TabMenu
              lists={[
                {
                  idx: 1,
                  key: 'employees',
                  name: 'Оповестить',
                },
                {
                  idx: 2,
                  key: 'responses',
                  name: (
                    <>
                      Отклики
                      {responsesCount > 0 && (
                        <span className={styles.tabs_badge}>{responsesCount}</span>
                      )}
                    </>
                  ),
                },
              ]}
              active={activeTab}
              setActive={handleTabChange}
            />
          </>
        )}
        {!hideWorkersForMarketplace ? (
          <>
            {isListLoading ? (
              <ContentLoader
                speed={2}
                height={56}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : worker ? (
              <div className={styles.selected}>
                <div className={styles.employee_details}>
                  <div className={styles.employee_header}>
                    <Avatar
                      name={worker?.name}
                      surname={worker?.surname}
                      avatar={worker?.worker_app?.avatar ?? worker?.avatar ?? null}
                      id={worker?.id}
                      statusActive={''}
                      height='82px'
                      width='82px'
                      isDeleted={worker?.is_deleted}
                    />
                    <div className={styles.employee_info}>
                      <div className={styles.employee_name_wrapper}>
                        <h3 className={styles.employee_name}>
                          {worker?.surname} {worker?.name} {worker?.patronymic}
                        </h3>
                        <p className={styles.employee_phone}>
                          {getFormatPhone(worker?.phone)}
                        </p>
                      </div>
                      {worker?.inn && (
                        <p className={styles.employee_inn}>ИНН {worker?.inn}</p>
                      )}
                      <p className={styles.risk_level}>Риск минимальный</p>
                    </div>
                  </div>

                  <div className={styles.status_indicators}>
                    {profileData?.settings?.tetrika ? (
                      <>
                        {isHasSignedContracts &&
                          isHasSignedContracts?.length > 0 &&
                          isHasSignedContracts?.map((contract, index) => (
                            <>
                              <div className={styles.status_item} key={index}>
                                <div className={styles.status_icon}>
                                  <CheckIcon />
                                </div>
                                <p>{contract.title}</p>
                              </div>
                            </>
                          ))}
                      </>
                    ) : (
                      <>
                        {' '}
                        {isHasSignedContract && (
                          <div className={styles.status_item}>
                            <div className={styles.status_icon}>
                              <CheckIcon />
                            </div>
                            <p>
                              Договор № УД-{workerForContract?.unified_contract?.number}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {!worker?.worker_app?.nationality ? (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <AlertIcon />
                        </div>
                        <p>Гражданство неизвестно. Обратитесь в поддержку</p>
                      </div>
                    ) : worker?.worker_app?.nationality !== 'Россия' ? (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <AlertIcon />
                        </div>
                        <p>
                          Не является гражданином России. Не забудь отправить уведомление
                          в МВД
                        </p>
                      </div>
                    ) : null}
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Является самозанятым</p>
                    </div>
                    {Boolean(worker?.has_ip) && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <WarningIcon />
                        </div>
                        <p>Есть открытое ИП</p>
                      </div>
                    )}
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Не состоит в списке экстремистов и террористов</p>
                    </div>
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Лимит дохода НПД 2,4 млн. не израсходован</p>
                    </div>
                    {worker?.has_orders_in_3_prev_months && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <AlertIcon />
                        </div>
                        <p style={{ color: '#F89500' }}>Заказы в последние 3 месяца</p>
                      </div>
                    )}
                    {data?.worker?.is_deleted && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <IconError style={{ width: '24px', height: '24px' }} />
                        </div>
                        <p style={{ color: '#e10d0d' }}>Профиль удален</p>
                      </div>
                    )}
                    <div>
                      <MinorAgeChecker data={orderWorker} showLabel={false} />
                    </div>
                  </div>

                  <p className={styles.report_date}>
                    Отчет на {moment(data?.status?.created_at).format('D MMMM YYYY')}
                  </p>
                </div>
              </div>
            ) : canPickWorker ? (
              <div>
                <div className={errors?.employee ? styles.border_error : undefined}>
                  <div className={styles.searchLine}>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <InputSearch
                        notFocus
                        value={searchValue}
                        placeholder='Искать по ИНН, ФИО или номеру телефона'
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                      />
                    </div>
                    <div className={styles.onlyMobile}>
                      <MobileMenu
                        filtersOnly
                        filtersOnlyIconOnly
                        mobileTabsSort={[]}
                        handleTabClickSort={mobileMenuFiltersOnlyStub}
                        activeSortName='default'
                        setActiveSort={mobileMenuFiltersOnlyStub}
                        dataFiltersMobile={dataFiltersMobile}
                        initialFilters={{
                          genders: filtersParams.genders ?? [],
                          issues: filtersParams.issues ?? [],
                          nationalities: filtersParams.nationalities ?? [],
                          categories: filtersParams.categories ?? [],
                          contracts: filtersParams.contracts ?? [],
                          date_add: filtersParams.date_add,
                        }}
                        showFilters={true}
                        onApplyFilters={(nextFilters) => {
                          setFiltersParams({
                            genders: nextFilters.genders ?? [],
                            issues: nextFilters.issues ?? [],
                            nationalities: nextFilters.nationalities ?? [],
                            categories: nextFilters.categories ?? [],
                            contracts: nextFilters.contracts ?? [],
                            date_add: nextFilters.date_add,
                          });
                          applySearchParams(buildMergedParams(searchValue, nextFilters));
                        }}
                        onResetFilters={() => {
                          const empty: EmployedsFilterParams = {
                            genders: [],
                            issues: [],
                            nationalities: [],
                            categories: [],
                            contracts: [],
                            date_add: undefined,
                          };
                          setFiltersParams(empty);
                          applySearchParams(buildMergedParams(searchValue, empty));
                        }}
                        mobileTabs={[]}
                        handleTabClick={mobileMenuFiltersOnlyStub}
                        activeTabName=''
                        toggleSearchOpen={mobileMenuFiltersOnlyStub}
                      />
                    </div>
                    <div className={styles.onlyDesktop}>
                      <Filters
                        dataFilters={
                          dataFilters?.filters ?? {
                            genders: [],
                            issues: [],
                            categories: [],
                            contracts: [],
                            nationalities: [],
                          }
                        }
                        notShowText={isMediumWidth || isSmallWidth}
                        showFilters={true}
                        initialFilters={{
                          genders: filtersParams.genders ?? [],
                          issues: filtersParams.issues ?? [],
                          nationalities: filtersParams.nationalities ?? [],
                          categories: filtersParams.categories ?? [],
                          contracts: filtersParams.contracts ?? [],
                          date_add: filtersParams.date_add,
                        }}
                        onApplyFilters={(nextFilters) => {
                          setFiltersParams({
                            genders: nextFilters.genders ?? [],
                            issues: nextFilters.issues ?? [],
                            nationalities: nextFilters.nationalities ?? [],
                            categories: nextFilters.categories ?? [],
                            contracts: nextFilters.contracts ?? [],
                            date_add: nextFilters.date_add,
                          });
                          applySearchParams(buildMergedParams(searchValue, nextFilters));
                        }}
                        onResetFilters={() => {
                          const empty: EmployedsFilterParams = {
                            genders: [],
                            issues: [],
                            nationalities: [],
                            categories: [],
                            contracts: [],
                            date_add: undefined,
                          };
                          setFiltersParams(empty);
                          applySearchParams(buildMergedParams(searchValue, empty));
                        }}
                      />
                    </div>
                  </div>

                  {width >= 700 &&
                    (activeTab === 'responses' && responsesCount <= 0 ? null : (
                      <div
                        className={
                          activeTab === 'employees'
                            ? styles.tableWrapperEmployees
                            : styles.tableWrapperResponses
                        }>
                        <p style={{ textAlign: 'left' }}>Исполнители</p>
                        {activeTab === 'employees' && (
                          <p style={{ textAlign: 'center' }}>Последний заказ, ₽</p>
                        )}
                        <p style={{ textAlign: 'right' }}>
                          {activeTab === 'employees'
                            ? 'Последние 3 мес, ₽'
                            : 'Выполнено / Дата регистрации'}
                        </p>
                      </div>
                    ))}

                  <div className={styles.employees_inner__table} ref={setTableListRef}>
                    {isListFetching ? (
                      <ContentLoader
                        speed={2}
                        height={'270px'}
                        width='100%'
                        className={styles.tab_loader}
                        backgroundColor={'#FFFFFF'}
                        foregroundColor={'#D8E4FB8C'}>
                        <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                      </ContentLoader>
                    ) : (
                      <>
                        {displayedWorkersList?.length ? (
                          displayedWorkersList.map((worker) => {
                            const isNotified = notifiedWorkersData?.worker_ids?.includes(
                              worker.id,
                            );
                            return (
                              <div
                                key={worker.id}
                                className={styles.employees_inner__table__item}>
                                {width >= 700 && (
                                  <div
                                    className={styles.employees_inner__table__item_user}>
                                    <div
                                      className={
                                        activeTab === 'employees'
                                          ? styles.userInfoEmployees
                                          : styles.userInfoResponses
                                      }>
                                      <InfoEmployeds
                                        status={worker.worker_app?.app_status}
                                        avatar={worker.worker_app?.avatar ?? null}
                                        id={worker.id}
                                        name={worker.name}
                                        surname={worker.surname}
                                        patronymic={worker.patronymic}
                                        phone={worker.phone ?? undefined}
                                        withPhoneIcon={false}
                                        birthdate={worker.worker_app.birthdate}
                                        nationality={worker.worker_app.nationality}
                                        textContractBanner={worker.risk?.text}
                                        colorBanner={worker.risk?.type}
                                      />
                                      {activeTab === 'employees' && (
                                        <div className={styles.lastOrder}>
                                          {worker.last_order_amount ? (
                                            <div>
                                              {FormatAmount({
                                                amount:
                                                  worker.last_order_amount.toString(),
                                                compact: true,
                                              })}
                                            </div>
                                          ) : (
                                            ''
                                          )}

                                          {worker.last_order_date && (
                                            <p style={{ paddingRight: '8px' }}>
                                              {formatEndDate(worker.last_order_date)}
                                            </p>
                                          )}
                                        </div>
                                      )}

                                      <div className={styles.last3Months}>
                                        {activeTab === 'employees' ? (
                                          worker.last_3_months_orders_count ? (
                                            <div
                                              className={styles.last3MonthsCount}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowLastOrder(
                                                  worker.id,
                                                  `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                                );
                                              }}>
                                              {worker.last_3_months_orders_count}{' '}
                                              {enumerate(
                                                worker.last_3_months_orders_count,
                                                ['заказ', 'заказа', 'заказов'],
                                              )}
                                            </div>
                                          ) : (
                                            ''
                                          )
                                        ) : worker.orders_count ? (
                                          <div>
                                            {worker.orders_count}{' '}
                                            {enumerate(worker.orders_count, [
                                              'заказ',
                                              'заказа',
                                              'заказов',
                                            ])}
                                          </div>
                                        ) : (
                                          <div style={{ height: '18px' }}></div>
                                        )}

                                        {activeTab === 'responses' && (
                                          <p>
                                            {formatEndDate(worker.worker_app.date_create)}
                                          </p>
                                        )}
                                      </div>

                                      {activeTab === 'employees' && (
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className={styles.notificationButton}
                                          style={{ alignItems: 'center' }}>
                                          <div
                                            onClick={() => {
                                              if (isNotified) return;
                                              handleNotifyWorker(worker.id);
                                            }}>
                                            {worker.risk?.type === 'black' ? null : (
                                              <div>
                                                {notifyingWorkerId === worker.id ? (
                                                  <Loader size='sm' color='#002CFB' />
                                                ) : isNotified ? (
                                                  <AppTooltip
                                                    label='Пользователь оповещён'
                                                    position='top'
                                                    offset={8}
                                                    arrowPosition='center'>
                                                    <IconSendFilled />
                                                  </AppTooltip>
                                                ) : (
                                                  <AppTooltip
                                                    label='Прислать оповещение'
                                                    position='top'
                                                    offset={8}
                                                    arrowPosition='center'>
                                                    <IconSend />
                                                  </AppTooltip>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {width < 700 && (
                                  <div
                                    className={
                                      styles.employees_inner__table__item_user_mobile
                                    }>
                                    <div className={styles.userInfoEmployedsMobile}>
                                      <InfoEmployeds
                                        status={worker.worker_app?.app_status}
                                        avatar={worker.worker_app?.avatar ?? null}
                                        id={worker.id}
                                        name={worker.name}
                                        surname={worker.surname}
                                        patronymic={worker.patronymic}
                                        phone={worker.phone ?? undefined}
                                        withPhoneIcon={false}
                                        birthdate={worker.worker_app.birthdate}
                                        nationality={worker.worker_app.nationality}
                                        textContractBanner={getWorkerStatus(worker)}
                                      />
                                      {activeTab === 'employees' && (
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className={styles.notificationButton}
                                          style={{ alignItems: 'flex-start' }}>
                                          <div
                                            onClick={() => {
                                              if (isNotified) return;
                                              handleNotifyWorker(worker.id);
                                            }}>
                                            <div>
                                              {notifyingWorkerId === worker.id ? (
                                                <Loader size='sm' color='#002CFB' />
                                              ) : isNotified ? (
                                                <AppTooltip
                                                  label='Пользователь оповещён'
                                                  position='top'
                                                  offset={8}
                                                  arrowPosition='center'>
                                                  <IconSendFilled />
                                                </AppTooltip>
                                              ) : (
                                                <AppTooltip
                                                  label='Прислать оповещение'
                                                  position='top'
                                                  offset={8}
                                                  arrowPosition='center'>
                                                  <IconSend />
                                                </AppTooltip>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {activeTab === 'employees' ? (
                                      <>
                                        <div className={styles.lastOrderMobile}>
                                          <IconWork />
                                          <div className={styles.lastOrderMobileInfo}>
                                            Последний заказ, ₽
                                            {worker.last_order_date && (
                                              <p>
                                                {formatEndDate(worker.last_order_date)}
                                              </p>
                                            )}
                                          </div>
                                          {worker.last_order_amount ? (
                                            <div className={styles.lastOrderMobileValue}>
                                              {FormatAmount({
                                                amount:
                                                  worker.last_order_amount.toString(),
                                                compact: true,
                                              })}
                                            </div>
                                          ) : null}
                                        </div>
                                        <div className={styles.lastOrderMobile}>
                                          <IconCalendar />
                                          <div className={styles.lastOrderMobileInfo}>
                                            Последние 3 мес
                                          </div>
                                          {worker.last_3_months_orders_count ? (
                                            <div
                                              className={styles.last3MonthsCount}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowLastOrder(
                                                  worker.id,
                                                  `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                                );
                                              }}>
                                              {worker.last_3_months_orders_count}{' '}
                                              {enumerate(
                                                worker.last_3_months_orders_count,
                                                ['заказ', 'заказа', 'заказов'],
                                              )}
                                            </div>
                                          ) : null}
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className={styles.lastOrderMobile}>
                                          <IconWork />
                                          <div className={styles.lastOrderMobileInfo}>
                                            Выполнено
                                            <p>Дата регистрации</p>
                                          </div>
                                          <div className={styles.lastOrderMobileValue}>
                                            {worker.orders_count ? (
                                              <>
                                                {worker.orders_count}{' '}
                                                {enumerate(worker.orders_count, [
                                                  'заказ',
                                                  'заказа',
                                                  'заказов',
                                                ])}
                                              </>
                                            ) : (
                                              <div style={{ height: '18px' }}></div>
                                            )}
                                            <p>
                                              {formatEndDate(
                                                worker.worker_app.date_create,
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className={styles.employees_inner__table__empty}>
                            {activeTab === 'employees'
                              ? 'Нет исполнителей'
                              : 'Пока никто не откликнулся на заказ'}
                          </div>
                        )}
                        {activeTab === 'employees' && workersFetching && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              padding: '10px 0',
                            }}>
                            <Loader size='sm' color='#002cfb' />
                          </div>
                        )}
                        {activeTab === 'responses' && isFetchingResponseWorkers && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              padding: '10px 0',
                            }}>
                            <Loader size='sm' color='#002cfb' />
                          </div>
                        )}
                        {activeTab === 'employees' && hasNextPageEmployees ? (
                          <div ref={intersectionRefEmployees} style={{ height: 10 }} />
                        ) : null}
                        {activeTab === 'responses' && hasNextPageResponses ? (
                          <div ref={intersectionRefResponses} style={{ height: 10 }} />
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
                <p className={styles.employees_inner__infoError}>{errors?.employee}</p>
              </div>
            ) : (
              <div className={styles.employees_inner__table__empty}>
                {activeTab === 'employees'
                  ? 'Нет исполнителей'
                  : 'Пока никто не откликнулся на заказ'}
              </div>
            )}
          </>
        ) : null}
      </div>
      {showModalApproveWorker && (
        <ModalApproveWorker
          open={showModalApproveWorker}
          setOpen={(open) => {
            setShowModalApproveWorker(open);
            if (!open) setPendingWorkerId(null);
          }}
          approveWorker={(id) => {
            selectWorker(id);
            handleToggleSelling(false);
            setShowModalApproveWorker(false);
            setPendingWorkerId(null);
          }}
          id={pendingWorkerId}
        />
      )}
      {textSuccessErrorInfo && (
        <SuccessErrorInfo
          text={textSuccessErrorInfo}
          handleClose={() => setTextSuccessErrorInfo(null)}
          type='success'
        />
      )}
      {!selectedWorker && (
        <div>
          {activeTab === 'employees' ? (
            <p className={styles.infoWorker}>
              Оповестить можно только готовых к сотрудничеству исполнителей
            </p>
          ) : (
            <p className={styles.infoWorker}>
              Подходящий исполнитель подбирается автоматически
            </p>
          )}
        </div>
      )}
    </div>
  );
};
